import { ArrowRight } from '@shared/icons';
import { useTranslations } from 'next-intl';
import NextLink from 'next/link';
import type { FooterAdditionalInformationSectionConfig } from '../../../../config/footer.types.ts';
import { Link } from '../../Link/Link';
import { ThemeAwareImage } from '../components/ThemeAwareImage';
import { ADDITIONAL_INFORMATION_IMAGES } from './additionalInformationImages';

type AdditionalInformationSectionProps = {
  config: FooterAdditionalInformationSectionConfig;
};

function AdditionalInformationSection({ config }: AdditionalInformationSectionProps) {
  const t = useTranslations('footer.additional_information');
  const { groups } = config;
  const DEFAULT_IMAGE_SIZE = 80;

  return (
    <div className="mt-6 mb-6 flex flex-col md:flex-wrap md:flex-row gap-6 w-full xl:gap-0 md:justify-between" data-track-container="Logos">
      {groups.map(group => (
        <div key={group.titleKey} className="min-w-0 max-w-full md:shrink-0 xl:basis-1/3 flex flex-col gap-3" data-track-container={group.trackName}>
          <div className="flex min-w-0 max-w-full flex-col items-start">
            <p className="paragraph-s">{t(group.titleKey)}</p>
            {group.link ? (
              <Link
                href={group.link.href}
                variant="secondary"
                size="s"
                data-track-name={group.link.trackName}
                data-testid="footer-additional-information-link"
                className="group inline max-w-full whitespace-normal"
              >
                {t(group.link.labelKey)}
                <ArrowRight
                  size="tiny"
                  className="ml-1 inline-block align-middle text-foreground-subtle group-hover:text-foreground-hover"
                  data-testid="footer-additional-information-link-arrow"
                />
              </Link>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-3">
            {group.items.map(item => {
              const image = (
                <ThemeAwareImage
                  key={item.imageKey}
                  className={item.className}
                  src={ADDITIONAL_INFORMATION_IMAGES[item.imageKey]}
                  alt={item.alt}
                  width={item.width ?? DEFAULT_IMAGE_SIZE}
                  height={item.height ?? DEFAULT_IMAGE_SIZE}
                />
              );

              if (item.href) {
                return (
                  <NextLink key={item.imageKey} href={item.href} data-track-name={item.trackName}>
                    {image}
                  </NextLink>
                );
              }

              return image;
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export { AdditionalInformationSection };
